// Dossier magazine — DEATH GRIP À MOTO
// Contenu original — Bruno · Équipe Code 2 Roues · 2026
import {
  Eye, Hand, AlertTriangle, Sparkles, Target, Compass,
  ShieldCheck, HelpCircle, Lightbulb, Zap, Bike, Activity,
  Wind, Repeat, TrendingUp, Users,
} from 'lucide-react';

export const deathGripArticle = {
  slug: 'death-grip-moto-crispation-guidon',
  weekVolume: 38,
  weekYear: 2026,
  monthLabel: 'Octobre 2026',
  category: 'Conseils moto & sécurité routière',
  shortTitle: 'Death grip — Relâcher le guidon',
  title: "Death grip à moto : pourquoi serrer trop fort le guidon nuit à votre conduite",
  kicker: '🏍️ Dossier technique · Signé Bruno · Équipe Code 2 Roues',
  subtitle:
    "Serrer le guidon de toutes ses forces peut sembler rassurant à moto. Pourtant, une crispation excessive des mains, des bras et des épaules peut <strong>perturber la précision des gestes et augmenter la fatigue</strong>. Ce phénomène, connu sous le nom de <em>death grip</em>, concerne aussi bien les débutants que les motards expérimentés.",
  heroImage: '/images/articles/death-grip-hero.jpg',
  heroVideo: '/videos/hero-death-grip.mp4',
  heroImageAlt: "Motard adoptant une posture souple au guidon de sa moto, coudes légèrement fléchis et épaules détendues — illustration pédagogique CODE 2 ROUES.",
  coverPoster: '/images/articles/death-grip-cover.jpg',
  coverPosterAlt: "Poster CODE 2 ROUES — Death grip à moto : reconnaître la crispation, relâcher les épaules, détendre les bras, répartir les appuis, regarder loin.",
  topRightKicker: { line1: 'Conseils & sécurité', line2: 'Posture · Souplesse' },
  readTimeMin: 9,
  publishedAt: '2026-10-15',
  seoDescription:
    "Pourquoi se crispe-t-on sur le guidon à moto ? Découvrez les signes du death grip et les conseils pour améliorer votre posture, votre souplesse et votre conduite.",
  seoKeywords:
    'death grip moto, crispation guidon moto, posture moto, conduite moto, sécurité deux-roues, formation moto, arm pump, syndrome des loges',
  about: "Dossier pédagogique — comprendre, reconnaître et corriger la crispation sur le guidon à moto.",
  chapo: {
    kicker: "L'idée à retenir",
    body:
      "La maîtrise d'une moto <strong>ne dépend pas de la force avec laquelle on serre le guidon</strong>, mais de la qualité et de la précision des gestes. Des épaules détendues, des coudes mobiles, des appuis équilibrés et un regard orienté vers la trajectoire contribuent à une conduite plus fluide et moins fatigante.",
    sideStat: {
      kicker: "La formule CODE 2 ROUES",
      big: 'MOINS DE FORCE,\nPLUS DE PRÉCISION',
      body: "Celui qui paraît le plus à l'aise ne force pas sur son guidon — il <strong>répartit ses efforts</strong>, respire, et laisse ses bras libres de leurs mouvements.",
      accent: '#cf2031',
    },
  },
  pullQuote: {
    text: "Lors de votre prochaine séance, soyez attentif à vos épaules, à vos avant-bras et à votre position sur la moto. Identifiez les moments où vous vous crispez — et travaillez ces points avec un enseignant qualifié, dans un environnement adapté.",
    source: 'Bruno · Équipe Code 2 Roues — Enseignant ECSR, CODE 2 ROUES',
  },
  sections: [
    {
      part: 'Partie 1 — Comprendre',
      title: "Qu'est-ce que le « death grip » à moto ?",
      intro:
        "Le terme anglais <em>death grip</em> désigne <strong>une prise excessivement ferme sur les poignées du guidon</strong>. Cette crispation apparaît souvent lorsque le pilote ressent du stress, manque de confiance ou rencontre une situation inhabituelle : virage, accélération, chaussée dégradée ou circulation dense.",
      compareCards: [
        {
          accent: '#cf2031',
          kicker: '❌ RÉFLEXE INSTINCTIF',
          title: 'Je serre plus fort pour mieux contrôler',
          intro: "Face au stress, le pilote crispé :",
          points: [
            { ok: false, text: "Avant-bras tendus en permanence" },
            { ok: false, text: "Multiplie les corrections involontaires" },
            { ok: false, text: "Perd en finesse et en fluidité" },
          ],
        },
        {
          accent: '#16a34a',
          kicker: '✅ GESTE MAÎTRISÉ',
          title: 'Je dose ma prise selon la situation',
          intro: "Le pilote souple :",
          points: [
            { ok: true, text: "Prise ferme mais sans excès" },
            { ok: true, text: "Mouvements du corps absorbés" },
            { ok: true, text: "Commandes utilisées avec précision" },
          ],
        },
      ],
      callouts: [
        {
          icon: Lightbulb,
          accent: '#f59e0b',
          body: "<strong>À retenir :</strong> une prise ferme est nécessaire pour contrôler les commandes, mais <em>une crispation permanente n'améliore pas la maîtrise</em> de la moto. Elle fatigue le pilote et dégrade la précision des gestes au fil du trajet.",
        },
      ],
    },
    {
      part: 'Partie 2 — Les signes',
      title: "Bonne vs mauvaise position : cliquez pour comparer",
      intro:
        "Avant de détailler les signes un par un, voici un visuel interactif : <strong>cliquez sur l'image pour basculer</strong> entre la bonne et la mauvaise position. Les différences sont instantanément lisibles — tensions, raideurs et regard proche d'un côté ; souplesse, mobilité et regard loin de l'autre.",
      compareToggle: {
        good: {
          src: '/images/articles/death-grip-hero.jpg',
          alt: "La bonne position à moto — épaules détendues, coudes légèrement fléchis, jambes en appui, prise souple, regard loin.",
          caption: "✅ <strong>La bonne position</strong> — plus de contrôle, plus de sécurité, plus de plaisir. Épaules détendues, coudes légèrement fléchis, jambes en appui, prise souple, regard loin. Cliquez sur l'image pour voir la mauvaise position.",
          label: 'Voir la mauvaise position',
        },
        bad: {
          src: '/images/articles/death-grip-mauvaise-position.jpg',
          alt: "La mauvaise position à moto — épaules remontées, bras tendus, poignées serrées, coudes verrouillés, appui excessif sur les mains, regard trop proche.",
          caption: "❌ <strong>La mauvaise position</strong> — plus de tension, plus de fatigue, moins de contrôle. Épaules remontées, bras tendus, poignées serrées, coudes verrouillés, appui excessif, regard trop proche. Cliquez pour revenir à la bonne position.",
          label: 'Voir la bonne position',
        },
      },
      callouts: [
        {
          icon: Sparkles,
          accent: '#16a34a',
          body: "<strong>Astuce lecture :</strong> l'image interactive ci-dessus vous permet d'alterner d'un simple tap. Chaque élément du corps libère les autres — ou les crispe. Les 6 pastilles ci-dessous détaillent chaque signe à surveiller.",
        },
      ],
    },
    {
      part: 'Partie 2 bis',
      title: "6 signes à reconnaître sur votre propre posture",
      intro:
        "Observez-vous honnêtement sur chaque trajet. <strong>6 signes identifiables</strong> peuvent transformer la conduite en effort permanent. <em>💡 Astuce : cliquez sur chaque pastille pour voir la bonne position correspondante.</em>",
      imageCards: [
        {
          img: '/images/articles/death-grip/07_regard_trop_proche.png',
          alt: 'Signe n°1 — Regard trop proche : focalisation sur la roue avant au lieu d\'anticiper la trajectoire',
          numero: '1',
          titre: 'Regard trop proche',
          description: 'Se focalise sur la roue avant au lieu d\'anticiper la trajectoire.',
          goodImg: '/images/articles/death-grip/bon/01_regard_loin.jpg',
          goodAlt: 'Regard porté loin — anticipation de la trajectoire',
          goodTitre: 'Regard porté loin',
          goodDescription: "Regardez vers la zone où vous souhaitez vous diriger. Un regard anticipé permet de mieux préparer sa trajectoire, de détecter les dangers et de conserver une conduite fluide.",
        },
        {
          img: '/images/articles/death-grip/01_epaules_remontees.png',
          alt: 'Signe n°2 — Épaules remontées : tension dans le haut du corps et fatigue plus rapide',
          numero: '2',
          titre: 'Épaules remontées',
          description: 'Tension dans le haut du corps et fatigue plus rapide.',
          goodImg: '/images/articles/death-grip/bon/02_epaules_detendues.jpg',
          goodAlt: 'Épaules détendues — haut du corps libre, fatigue réduite',
          goodTitre: 'Épaules détendues',
          goodDescription: "Gardez les épaules basses et relâchées. Une crispation excessive augmente la fatigue et rend les gestes moins fluides. Respirez régulièrement.",
        },
        {
          img: '/images/articles/death-grip/04_coudes_verrouilles.png',
          alt: 'Signe n°3 — Bras tendus et coudes verrouillés : limite la souplesse et la précision de pilotage',
          numero: '3',
          titre: 'Bras tendus & coudes verrouillés',
          description: 'Moins de mobilité pour accompagner les mouvements, précision limitée.',
          goodImg: '/images/articles/death-grip/bon/03_coudes_bras_souples.jpg',
          goodAlt: 'Coudes légèrement fléchis — mobilité et précision préservées',
          goodTitre: 'Coudes légèrement fléchis',
          goodDescription: "Évitez de verrouiller les bras. Des coudes légèrement fléchis permettent de conserver de la mobilité et d'accompagner les mouvements de pilotage.",
        },
        {
          img: '/images/articles/death-grip/03_poignees_serrees.png',
          alt: 'Signe n°4 — Poignées serrées : transmet les chocs et les vibrations à la direction',
          numero: '4',
          titre: 'Poignées serrées',
          description: 'Transmet les chocs et les vibrations à la direction.',
          goodImg: '/images/articles/death-grip/bon/04_prise_souple.jpg',
          goodAlt: 'Prise souple sur les poignées — dosée selon la situation',
          goodTitre: 'Prise souple du guidon',
          goodDescription: "Tenez les poignées suffisamment fermement pour contrôler les commandes, sans les serrer excessivement. Les poignets restent confortables pour actionner gaz, embrayage et freins.",
        },
        {
          img: '/images/articles/death-grip/06_fatigue_ou_douleurs.png',
          alt: 'Signe n°5 — Dos crispé : posture déséquilibrée, tensions dans la nuque et le haut du corps',
          numero: '5',
          titre: 'Dos crispé',
          description: 'Buste trop penché ou affaissé, tensions dans la nuque, fatigue sur longs trajets.',
          goodImg: '/images/articles/death-grip/bon/05_dos_neutre.jpg',
          goodAlt: 'Dos en position naturelle — posture équilibrée et endurante',
          goodTitre: 'Dos dans une position naturelle',
          goodDescription: "Conservez une posture équilibrée, sans vous affaisser ni vous pencher excessivement sur le guidon. Votre buste doit rester adapté à la géométrie de la moto.",
        },
        {
          img: '/images/articles/death-grip/05_appui_excessif_mains.png',
          alt: 'Signe n°6 — Appui excessif sur les mains : le poids du buste repose trop sur le guidon',
          numero: '6',
          titre: 'Appui excessif sur les mains',
          description: 'Le poids du buste repose trop sur le guidon, les mains sont surchargées.',
          goodImg: '/images/articles/death-grip/bon/06_jambes_appui.jpg',
          goodAlt: 'Jambes en appui — poids réparti entre jambes et bras',
          goodTitre: 'Des jambes qui participent à la stabilité',
          goodDescription: "Les jambes contribuent au maintien du corps sur la moto. Le contact des cuisses avec le réservoir permet de limiter les appuis excessifs sur les bras, notamment au freinage.",
        },
      ],
      imageCardsCols: 3,
      callouts: [
        {
          icon: Lightbulb,
          accent: '#f59e0b',
          body: "<strong>Ces signes peuvent apparaître progressivement</strong>, souvent sans que l'on s'en rende compte. Les identifier permet de corriger sa posture et de retrouver <em>une conduite plus fluide et moins fatigante</em>.",
        },
      ],
    },
    {
      part: 'Partie 2 ter',
      title: "3 autres clés d'une bonne position",
      intro:
        "Au-delà des 6 signes principaux, trois éléments complètent une posture efficace à moto : <strong>les genoux, les pieds et la posture générale</strong>. Ils participent à la stabilité, à la fluidité des gestes et à l'économie d'efforts.",
      imageCards: [
        {
          img: '/images/articles/death-grip/bon/07_genoux.jpg',
          alt: 'Genoux naturellement positionnés — stabilité sans crispation',
          titre: '7 · Genoux naturellement positionnés',
          description: "Gardez les genoux dans une position confortable, compatible avec la morphologie de la moto. Évitez les mouvements parasites et les crispations inutiles. Stabilité ne signifie pas crispation.",
        },
        {
          img: '/images/articles/death-grip/bon/08_pieds.jpg',
          alt: 'Pieds correctement positionnés sur les repose-pieds',
          titre: '8 · Pieds correctement positionnés',
          description: "Placez les pieds sur les repose-pieds de manière stable et confortable. Gardez-les prêts à utiliser les commandes, sans laisser inutilement la pointe du pied sous le sélecteur.",
        },
        {
          img: '/images/articles/death-grip/bon/09_posture_generale.jpg',
          alt: 'Posture générale équilibrée — motard sur sa moto en profil',
          titre: '9 · Posture générale équilibrée',
          description: "La bonne position résulte de l'ensemble : regard anticipé, épaules détendues, bras mobiles, buste équilibré et appuis adaptés. Chaque élément contribue à une conduite plus fluide.",
        },
      ],
      imageCardsCols: 3,
      callouts: [
        {
          icon: Sparkles,
          accent: '#16a34a',
          body: "<strong>Conclusion :</strong> la bonne position à moto n'est pas une pose figée mais un <em>équilibre vivant</em>. Elle doit toujours être ajustée à la moto, à la vitesse et aux conditions de circulation. Tout le corps participe au pilotage — chaque segment libère les autres, ou les crispe. Observez-vous, respirez, et laissez vos gestes retrouver leur fluidité naturelle.",
        },
      ],
    },
    {
      part: 'Partie 3 — Pourquoi',
      title: "Pourquoi faut-il garder les bras souples ?",
      intro:
        "Les bras jouent un rôle essentiel dans le pilotage. Ils permettent d'actionner les commandes et d'exercer les actions nécessaires sur le guidon. <strong>Excessivement contractés, ils limitent la liberté de mouvement du pilote.</strong>",
      body:
        "<p>Les irrégularités de la chaussée, les mouvements du corps et les tensions musculaires peuvent alors compliquer les actions. Cette rigidité <strong>accélère également la fatigue</strong>. Or, un pilote fatigué risque de perdre en précision et en disponibilité au fil du trajet.</p><p>L'objectif n'est pas de conduire avec les bras mous, mais de conserver <strong>une tension musculaire adaptée à la situation</strong>, sans effort inutile.</p>",
      callouts: [
        {
          icon: Wind,
          accent: '#0ea5e9',
          body: "<strong>L'image à retenir :</strong> imaginez vos bras comme deux suspensions. Elles doivent absorber les mouvements, pas les transmettre. Trop rigides, elles amplifient les vibrations au lieu de les filtrer.",
        },
      ],
    },
    {
      part: 'Partie 4 — La correction',
      title: 'Comment corriger le death grip ? La méthode CODE 2 ROUES',
      intro:
        "La correction repose principalement sur <strong>la posture, la répartition des appuis et la prise des poignées</strong>. Voici 4 axes à travailler, idéalement avec un enseignant qualifié dans un cadre sécurisé.",
      orderedList: [
        "<strong>Détendez les épaules et gardez les coudes légèrement fléchis.</strong> Les épaules doivent rester basses et détendues. Les coudes ne doivent pas être verrouillés : ils doivent conserver suffisamment de mobilité pour accompagner les actions de pilotage. Les poignets restent dans une position confortable, permettant d'utiliser les commandes sans contrainte excessive.",
        "<strong>Stabilisez votre corps avec des appuis adaptés.</strong> Le pilote ne doit pas utiliser ses bras comme unique point d'ancrage. Selon la moto et la position de conduite, les jambes peuvent contribuer à stabiliser le corps. Le contact des genoux et des cuisses avec le réservoir, lorsqu'il est adapté à la machine, permet de limiter les appuis excessifs sur les mains. Il ne s'agit pas de serrer le réservoir en permanence, mais de <em>répartir les efforts</em> de manière plus équilibrée.",
        "<strong>Regardez loin et anticipez.</strong> Le regard joue un rôle central dans la conduite d'un deux-roues. Une vision dirigée vers la zone à atteindre aide le pilote à anticiper sa trajectoire et à organiser ses actions. À l'inverse, une attention excessive portée au guidon ou à la roue avant favorise une conduite hésitante et tendue. Regarder loin ne dispense jamais de surveiller l'environnement proche, les dangers et les autres usagers.",
        "<strong>Respirez et relâchez les tensions inutiles.</strong> Le stress peut entraîner une contraction involontaire des muscles. Une respiration régulière et une attention portée aux épaules et aux avant-bras aident à identifier cette crispation. Sur une portion de route adaptée, lorsque la circulation et les conditions le permettent, le pilote peut vérifier mentalement sa posture <em>sans détourner son attention de la conduite</em>.",
      ],
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body: "<strong>Attention :</strong> ne cherchez jamais à vous détendre au point de perdre la maîtrise des commandes. Ne réalisez pas d'exercice de relâchement dans une situation dangereuse. La sécurité passe avant toute démarche de confort.",
        },
      ],
    },
    {
      part: 'Partie 5 — La formation',
      title: 'Comment travailler cette compétence en formation moto ?',
      intro:
        "La lutte contre la crispation fait partie de l'apprentissage d'une conduite fluide et maîtrisée. Lors des séances pratiques, <strong>plusieurs axes de travail</strong> peuvent être abordés avec un enseignant qualifié.",
      cards: [
        { icon: Target, accent: '#0ea5e9', kicker: '🎯 Axe 1', title: 'Travail de posture', body: "Observer la position des épaules, des coudes, des poignets et des appuis pour repérer les tensions inutiles." },
        { icon: Bike, accent: '#0ea5e9', kicker: '🏍️ Axe 2', title: 'Exercices à allure réduite', body: "Dans un espace adapté et sécurisé, des exercices progressifs améliorent équilibre, coordination et souplesse." },
        { icon: Eye, accent: '#0ea5e9', kicker: '👀 Axe 3', title: 'Regard et trajectoires', body: "Apprendre à anticiper, orienter son regard et coordonner les actions sur les commandes avec la trajectoire souhaitée." },
        { icon: Users, accent: '#0ea5e9', kicker: '🧑‍🏫 Axe 4', title: 'Retours pédagogiques', body: "L'enseignant identifie les situations de crispation et propose des corrections adaptées au niveau de l'élève." },
      ],
      cardsCols: 2,
      callouts: [
        {
          icon: ShieldCheck,
          accent: '#16a34a',
          body: "Ces exercices doivent <strong>toujours respecter les consignes de sécurité</strong> et être adaptés à la moto utilisée ainsi qu'aux capacités du conducteur.",
        },
      ],
    },
    {
      part: 'Partie 6 — Alerte médicale',
      title: 'Le syndrome des loges : quand les douleurs doivent alerter',
      intro:
        "Chez certains pilotes, notamment en compétition, des efforts intenses et répétés peuvent être associés à un <strong>syndrome des loges chronique d'effort de l'avant-bras</strong>, également appelé <em>arm pump</em>.",
      body:
        "<p>Cette affection peut provoquer une douleur importante, une sensation de pression, des fourmillements ou une diminution de la force pendant l'effort. <strong>Elle ne doit pas être confondue avec le simple fait de serrer trop fort le guidon.</strong> Le syndrome des loges est une pathologie médicale qui nécessite une évaluation spécifique.</p>",
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body: "<strong>Si des douleurs persistent après la conduite</strong> ou réapparaissent régulièrement, demandez un avis médical. En cas de douleur intense ou de perte brutale de force ou de sensibilité, faites-vous évaluer rapidement par un professionnel de santé.",
        },
      ],
    },
    {
      part: 'Partie 7 — Les équipements',
      title: 'Les équipements peuvent-ils aider ?',
      intro:
        "Certains équipements peuvent contribuer au confort, <strong>sans remplacer un travail sur la posture</strong>. Les poignées adaptées, le réglage des commandes et une position de conduite appropriée peuvent faciliter la prise en main.",
      body:
        "<p>Des <strong>grips de réservoir</strong> peuvent également améliorer l'adhérence entre les jambes et la moto lorsque leur forme et leur matériau sont adaptés au modèle utilisé.</p><p>Toute modification doit préserver la liberté de mouvement, le bon fonctionnement des commandes et la sécurité du pilote.</p>",
      callouts: [
        {
          icon: HelpCircle,
          accent: '#f59e0b',
          body: "<strong>Un équipement ne corrige pas à lui seul une crispation</strong> liée au stress ou à une mauvaise posture. Il complète la méthode — il ne la remplace pas.",
        },
      ],
    },
    {
      part: 'À retenir',
      title: 'La méthode CODE 2 ROUES en 5 réflexes',
      cards: [
        { icon: Wind, accent: '#0ea5e9', kicker: '💨', title: 'JE RESPIRE', body: "Je prends conscience de mon souffle et de mon rythme cardiaque." },
        { icon: Activity, accent: '#cf2031', kicker: '💪', title: 'JE RELÂCHE', body: "Je décontracte les épaules, détends les avant-bras et libère les coudes." },
        { icon: Eye, accent: '#7c3aed', kicker: '👀', title: 'JE REGARDE LOIN', body: "Mon regard anticipe la trajectoire, pas le guidon." },
        { icon: ShieldCheck, accent: '#16a34a', kicker: '🛡️', title: 'JE RÉPARTIS', body: "Mes appuis sont répartis entre jambes et bras, pas tout sur les mains." },
        { icon: Sparkles, accent: '#f59e0b', kicker: '✨', title: 'JE FLUIDIFIE', body: "Je dose ma prise selon la situation — plus de précision, moins de force." },
      ],
      cardsCols: 5,
      callouts: [
        {
          icon: Sparkles,
          accent: '#cf2031',
          body: "<strong>Que vous soyez débutant ou motard expérimenté</strong>, apprendre à reconnaître vos tensions et à les corriger progressivement constitue un véritable atout pour votre sécurité et pour le plaisir de rouler.",
        },
      ],
    },
  ],
  ctaFinal: {
    kicker: 'Pratique guidée',
    title: 'Travaillez votre souplesse avec un enseignant qualifié',
    body: "Le <strong>death grip</strong> se corrige en séance avec un enseignant qualifié dans un cadre sécurisé. Nos dossiers <strong>Équilibre à moto</strong> et <strong>Contre-braquage</strong> complètent cette approche — ensemble, ils couvrent la relation complète entre votre corps et votre moto.",
    primary: { label: 'Lire le dossier Équilibre', to: '/magazine/equilibre-moto-basse-vitesse' },
    secondary: { label: 'Voir le dossier Contre-braquage', to: '/magazine/contre-braquage-moto-methode' },
  },
};
